import { Fragment } from "react";
import { useStoryblokState, getStoryblokApi } from "@storyblok/react";
import {
  dehydrate,
  QueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { sbBlok } from "../../types/sb-types";
import BackgroundGraphic from "../../components/BackgroundGraphic";
import GridWrapper from "../../components/GridWrapper";
import GridElement from "../../components/GridElement";
import { motion } from "framer-motion";
import styles from "../../styles/News.module.css";
import { useWindowSize } from "../../hooks/useWindowSize";
import Link from "next/link";
import BlogPost from "../../components/BlogPost/BlogPost";
import Cta from "../../components/Cta/Cta";

const sbQueryParams = {
  starts_with: "news",
  is_startpage: "false",
  resolve_links: "url",
  sort_by: "first_published_at:desc",
  per_page: 13,
};

export default function NewsHomePage({ story, isTestEnv }: any) {
  story = useStoryblokState(story);
  const storyblokApi = getStoryblokApi();
  const {
    data: newsStories,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    ["news"],
    ({ pageParam = 1 }) => {
      return storyblokApi.get(`cdn/stories`, {
        ...sbQueryParams,
        page: pageParam,
        version: isTestEnv ? "draft" : "published",
      });
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.total / lastPage.perPage > pages.length) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    }
  );
  const { isDesktop } = useWindowSize();

  return (
    <GridWrapper className={styles.news}>
      <GridElement gridLayout={[[1401, 4, 26]]}>
        <BackgroundGraphic align="right" />
        {newsStories?.pages && (
          <BlogPost
            isLarge
            ctaType="button"
            blok={{
              _uid: newsStories?.pages[0].data.stories[0].content._uid,
              component:
                newsStories?.pages[0].data.stories[0].content.component,
              image:
                newsStories?.pages[0].data.stories[0].content.preview_image,
              meta_title:
                newsStories?.pages[0].data.stories[0].content
                  .preview_info_title,
              title:
                newsStories?.pages[0].data.stories[0].content.preview_title,
              intro:
                newsStories?.pages[0].data.stories[0].content.preview_intro,
              cta_link: [
                {
                  name: "Read more",
                  link: {
                    linktype: "story",
                    story: {
                      full_slug:
                        newsStories?.pages[0].data.stories[0].full_slug,
                    },
                  },
                },
              ],
            }}
          />
        )}
        <motion.section
          className={styles.blogPostsSmall}
          viewport={{ once: false }}
          initial={{
            opacity: isDesktop ? 1 : 0.3,
            transform: isDesktop ? "translate(0, 0)" : "translate(0, 6rem)",
          }}
          whileInView={{
            opacity: 1,
            transform: "translate(0, 0)",
          }}
          transition={{
            ease: "linear",
            duration: 0.8,
          }}
        >
          {newsStories?.pages?.map((group, i) => {
            return (
              <Fragment key={`${group.data.stories[0].uuid}-${i}`}>
                {group.data.stories.map((story: sbBlok, n: number) => {
                  if (i === 0 && n === 0) return;
                  return (
                    <Link
                      key={`${story.content._uid}-${n}`}
                      href={story.full_slug}
                    >
                      <a className={styles.blogPostsSmallLink}>
                        <BlogPost
                          isLarge={false}
                          blok={{
                            _uid: story.content._uid,
                            component: story.content.component,
                            image: story.content.preview_image,
                            meta_title: story.content.preview_info_title,
                            title: story.content.preview_title,
                            intro: story.content.preview_intro,
                          }}
                        />
                      </a>
                    </Link>
                  );
                })}
              </Fragment>
            );
          })}
        </motion.section>
        {(isLoading || isFetchingNextPage) && (
          <>
            <BlogPost isLoading />
            <BlogPost isLoading />
            <BlogPost isLoading />
          </>
        )}
        {hasNextPage && (
          <div className={styles.buttonWrapper}>
            <Cta
              name="Show more"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            />
          </div>
        )}
      </GridElement>
    </GridWrapper>
  );
}

export async function getStaticProps() {
  const sbParams = {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
    resolve_links: "url",
  };

  const storyblokApi = getStoryblokApi();
  const { data } = await storyblokApi.get(`cdn/stories/news`, sbParams);

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(["news"], () =>
    storyblokApi.get(`cdn/stories/`, {
      ...sbQueryParams,
      version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
      page: 1,
      per_page: 12,
    })
  );

  return {
    props: {
      story: data ? data.story : false,
      key: data ? data.story.id : false,
      isTestEnv: process.env.VERCEL_ENV !== "production",
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 10,
  };
}
